import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Paperclip, Loader2, FileText, Download, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { FileThumb } from "./FileThumb";
import { useLightbox, canPreview } from "./FileLightbox";

const prettySize = (n) => {
  n = Number(n) || 0;
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
};

/**
 * ChatAttachButton — a file in chat has to belong to a deal. Pick the case, pick the file,
 * and it's filed on that case like any other document; the thread only carries the receipt.
 * Exposes openWith(file) so a file dropped anywhere on the dock lands here already staged.
 */
export const ChatAttachButton = React.forwardRef(({ casesPath, uploadPath, onSent, testPrefix = "chat-attach", width = 320 }, ref) => {
  const [open, setOpen] = useState(false);
  const [cases, setCases] = useState(null);
  const [leadId, setLeadId] = useState("");
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useImperativeHandle(ref, () => ({
    openWith(dropped) {
      if (dropped) setFile(dropped);
      setOpen(true);
    },
  }));

  useEffect(() => {
    if (!open || cases) return;
    api.get(casesPath).then((r) => {
      const items = r.data.items || [];
      setCases(items);
      if (items.length === 1) setLeadId(items[0].id);
    }).catch(() => setCases([]));
  }, [open, cases, casesPath]);

  const reset = () => { setOpen(false); setFile(null); setCaption(""); };

  const send = async () => {
    if (!file || !leadId) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("lead_id", leadId);
      if (caption.trim()) fd.append("caption", caption.trim());
      await api.post(uploadPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sent — and filed on the case");
      reset();
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send that file"); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative shrink-0">
      <button onClick={() => (open ? reset() : setOpen(true))} title="Attach a document to a case"
              className={`grid place-items-center size-10 rounded-xl border transition-colors ${
                open ? "border-[#0055FF]/50 bg-[#0055FF]/[0.06] text-[#0055FF]"
                     : "border-slate-200 bg-white text-slate-500 hover:border-[#0055FF]/40 hover:text-[#0055FF]"}`}
              data-testid={`${testPrefix}-open`}>
        <Paperclip className="size-4" />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 z-20 mb-2 max-w-[calc(100vw-2.5rem)] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-slate-900/10 space-y-3"
             style={{ width }} data-testid={`${testPrefix}-panel`}>
          <div className="flex items-center justify-between">
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-600">Attach to a case</div>
            <button onClick={reset} className="p-1 rounded hover:bg-slate-100 text-slate-400" data-testid={`${testPrefix}-close`}>
              <X className="size-3.5" />
            </button>
          </div>

          {cases === null ? (
            <div className="grid place-items-center py-4 text-slate-400"><Loader2 className="size-4 animate-spin" /></div>
          ) : cases.length === 0 ? (
            <div className="rounded-xl bg-slate-50 px-3 py-3 text-[12.5px] leading-relaxed text-slate-500">
              A document has to sit on one of your live cases so it never gets lost. You don&apos;t have an
              open case yet — send a message and the desk will pick it up.
            </div>
          ) : (
            <>
              <Select value={leadId} onValueChange={setLeadId}>
                <SelectTrigger className="h-auto min-h-10 py-2 text-left text-[12.5px] leading-snug whitespace-normal [&>span]:line-clamp-none" data-testid={`${testPrefix}-case`}>
                  <SelectValue placeholder="Which case is this for?" />
                </SelectTrigger>
                <SelectContent className="max-h-56 w-[var(--radix-select-trigger-width)]" position="popper" sideOffset={6}>
                  {cases.map((c) => <SelectItem key={c.id} value={c.id} className="text-[12.5px] leading-snug whitespace-normal">{c.label}</SelectItem>)}
                </SelectContent>
              </Select>

              <div onClick={() => fileRef.current?.click()}
                   className="rounded-xl border-2 border-dashed border-slate-300 px-3 py-3 text-center cursor-pointer hover:border-[#0055FF]/40 transition-colors"
                   data-testid={`${testPrefix}-drop`}>
                <div className="text-[12.5px] font-medium leading-snug text-slate-700 break-words">
                  {file ? file.name : "Choose a file (PDF, JPG, PNG, DOC, XLS)"}
                </div>
                {file && <div className="text-[11px] text-slate-400 mt-0.5">{prettySize(file.size)}</div>}
                <input ref={fileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv"
                       onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }}
                       data-testid={`${testPrefix}-file`} />
              </div>

              <textarea value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={300} rows={3}
                        placeholder="Add a note — e.g. indicative terms attached"
                        className="block w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-[12.5px] leading-relaxed text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-ring"
                        data-testid={`${testPrefix}-caption`} />

              <Button onClick={send} disabled={busy || !file || !leadId}
                      className="w-full rounded-xl bg-[#0055FF] hover:bg-[#0044CC] font-semibold"
                      data-testid={`${testPrefix}-submit`}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : "Send and file it on the case"}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
});
ChatAttachButton.displayName = "ChatAttachButton";

/** ChatAttachment — the receipt inside a message bubble. Readable files open in place. */
export const ChatAttachment = ({ att, mine }) => {
  const { openOrDownload, lightbox } = useLightbox();
  if (!att) return null;
  const readable = canPreview(att.filename, att.content_type);

  return (
    <>
      <button onClick={(e) => { e.stopPropagation(); openOrDownload({ id: att.file_id, filename: att.filename, content_type: att.content_type }); }}
              className={`mt-1.5 w-full flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                mine ? "border-white/25 bg-white/10 hover:bg-white/20"
                     : "border-slate-200 bg-slate-50 hover:border-[#0055FF]/40"}`}
              data-testid={`chat-attachment-${att.file_id}`}>
        <FileThumb fileId={att.file_id} alt={att.filename} size={38} hasThumb={att.has_thumb}
                   fallback={<FileText className={`size-4 shrink-0 ${mine ? "text-white/80" : "text-slate-400"}`} />} />
        <span className="flex-1 min-w-0">
          <span className={`block text-[12.5px] font-semibold truncate ${mine ? "text-white" : "text-slate-900"}`}>
            {att.filename}
          </span>
          <span className={`block text-[10.5px] truncate ${mine ? "text-white/60" : "text-slate-500"}`}>
            {prettySize(att.size)} · on {att.lead_label}
          </span>
        </span>
        {readable
          ? <Eye className={`size-3.5 shrink-0 ${mine ? "text-white/70" : "text-slate-400"}`} />
          : <Download className={`size-3.5 shrink-0 ${mine ? "text-white/70" : "text-slate-400"}`} />}
      </button>
      {lightbox}
    </>
  );
};
